import moment from 'moment';
import _ from 'lodash';
import i18next from 'i18next';
import 'src/i18n';

export const DATE_PATTERN = 'MMM/DD';
const PARSE_DATE_PATTERN = 'M/D';
const FIX_MONTH = 8;

export const getDaysLabel = days =>
  days.map(item => {
    const label = item.value !== 31 ? `${item.label}` : i18next.t('reducers.selectOptions.last_day');
    return { value: item.value, label };
  });

const shouldSortDateForReCalculate = date => {
  if (!date?.firstPaymentDate && !date?.secondPaymentDate) return false;
  const { firstPaymentDate, secondPaymentDate } = date;
  return secondPaymentDate.get('D') < firstPaymentDate.get('D');
};

export const parseDate = day => {
  if (!_.isNumber(day)) return moment(`${FIX_MONTH}/31`, PARSE_DATE_PATTERN);
  const adjustedDay = day > 31 ? 31 : day;
  return moment(`${FIX_MONTH}/${adjustedDay}`, PARSE_DATE_PATTERN);
};

const isDateAfterEndOfMonth = date =>
  date >
  moment(FIX_MONTH, 'M')
    .endOf('month')
    .date();

const formatDate = (date, shouldSortDate = false, paymentMonthType) => {
  const { firstStartDate, firstEndDate, firstPaymentDate, secondStartDate, secondEndDate, secondPaymentDate } = date;
  const isNextMonth = paymentMonthType === 'next_month';
  const secondHalfPaymentDay = secondPaymentDate.get('D');
  const secondHalfCutOffDay = secondEndDate.get('D');

  return {
    firstHalf: {
      startDate: shouldSortDate ? secondStartDate.clone().subtract(1, 'months') : firstStartDate,
      endDate: shouldSortDate ? secondEndDate.clone().subtract(1, 'months') : firstEndDate,
      paymentDate: shouldSortDate
        ? isNextMonth
          ? secondPaymentDate
          : secondHalfPaymentDay <= secondHalfCutOffDay
            ? secondPaymentDate
            : secondPaymentDate.clone().subtract(1, 'months')
        : firstPaymentDate
    },
    secondHalf: {
      startDate: shouldSortDate ? firstStartDate : secondStartDate,
      endDate: shouldSortDate ? firstEndDate : secondEndDate,
      paymentDate: shouldSortDate ? firstPaymentDate : secondPaymentDate
    }
  };
};

export const getClosingDaySecondHalf = date => {
  const endOfMonth = moment(FIX_MONTH, 'M')
    .endOf('month')
    .get('D');
  const paymentDate = parseDate(date);

  if (!paymentDate.isValid()) return null;
  const isBeforeOrOnEndOfMonth = endOfMonth >= date;
  const closingDay = isBeforeOrOnEndOfMonth ? paymentDate.subtract(1, 'days') : paymentDate.add(1, 'months').subtract(1, 'days');
  return closingDay.get('D');
};

export const calculatePaymentDateOptions = ({ startDay, endDay, isSecondHalf, isNextMonth = false }) => {
  if (!startDay && !endDay) return [];
  const paymentDate = [];
  const firstDay = !isNextMonth && isSecondHalf ? startDay + 1 : startDay;
  if (firstDay <= endDay) {
    for (let i = firstDay; i <= endDay; i += 1) {
      paymentDate.push({ value: i, label: i });
    }
  } else {
    for (let i = firstDay; i <= 31 + endDay; i += 1) {
      const day = i > 31 ? i - 31 : i;
      paymentDate.push({ value: day, label: day });
    }
  }
  return getDaysLabel(paymentDate);
};

export const calculateDateTerm = ({
  closingDay,
  paymentDay,
  paymentDaySecondHalf,
  firstHalfStartDate,
  closingDaySecondHalf,
  isNextMonth = false
}) => {
  const startDate = parseDate(firstHalfStartDate || closingDaySecondHalf);
  const closingDate = parseDate(closingDay);
  const endOfFixMonth = moment(FIX_MONTH, 'M').endOf('month');

  const firstStartDate = startDate.isAfter(closingDate) ? startDate.clone().subtract(1, 'months') : startDate;
  const firstEndDate = closingDate.isValid() ? closingDate : endOfFixMonth;
  const firstPaymentDate = parseDate(paymentDay);

  if (isNextMonth && paymentDay && paymentDay < closingDay) {
    firstStartDate.subtract(1, 'months');
    firstEndDate.subtract(1, 'months');
  }

  if (!isNextMonth && paymentDay > closingDay) {
    firstPaymentDate.subtract(1, 'months');
  }

  const secondStartDate = firstEndDate.clone().add(1, 'days');
  const secondEndDate = firstStartDate
    .clone()
    .add(1, 'months')
    .subtract(1, 'days');
  const secondPaymentDate = parseDate(paymentDaySecondHalf);

  return {
    firstStartDate,
    firstEndDate,
    firstPaymentDate,
    secondStartDate,
    secondEndDate,
    secondPaymentDate
  };
};

export const sortingPreviewDate = ({ closingDay, paymentDay, closingDaySecondHalf, paymentDaySecondHalf }) => {
  const numberOfPreviousMonth =
    moment(FIX_MONTH, 'M')
      .subtract(1, 'months')
      .month() + 1;

  const secondCutOffDateFormat = isDateAfterEndOfMonth(closingDaySecondHalf)
    ? `${numberOfPreviousMonth}/${closingDaySecondHalf}`
    : closingDaySecondHalf;

  return {
    closingDay: secondCutOffDateFormat,
    paymentDay: paymentDaySecondHalf,
    closingDaySecondHalf: closingDay,
    paymentDaySecondHalf: paymentDay
  };
};

export const calculateDateForPreview = props => {
  const { paymentMonthType } = props;
  if (!paymentMonthType) return null;
  const isNextMonth = paymentMonthType === 'next_month';
  const calculatedDate = calculateDateTerm({ ...props, isNextMonth });
  const shouldSortDate = shouldSortDateForReCalculate(calculatedDate);
  return formatDate(calculatedDate, shouldSortDate, props.paymentMonthType);
};

export const sortingFormDate = form => {
  const { paymentDaySecondHalf, paymentDay, closingDaySecondHalf, closingDay } = form;
  if (paymentDaySecondHalf < paymentDay) {
    return {
      ...form,
      closingDay: closingDaySecondHalf,
      paymentDay: paymentDaySecondHalf,
      closingDaySecondHalf: closingDay,
      paymentDaySecondHalf: paymentDay,
      calcSscSeparately: form.timingOfTaxDeduction,
      calcWhtSeparately: form.timingOfTaxDeduction
    };
  }
  return {
    ...form,
    calcSscSeparately: form.timingOfTaxDeduction,
    calcWhtSeparately: form.timingOfTaxDeduction
  };
};

export const calculateDateForForm = form => {
  const firstHalfStartDate = form?.closingDaySecondHalf + 1 > 31 ? 1 : form?.closingDaySecondHalf + 1;
  return {
    firstHalfStartDate
  };
};
